import React, { useState, useRef, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { useAuth } from '../../contexts/AuthContext';
import { sanitise } from '../../utils/sanitise';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { deriveLifecycle, catchRefusalMessage, CLUB_TIMEZONE } from '@shared/competitionRules';
import { isLengthUnusual, isOverMaxLength, MAX_CATCH_LENGTH_MESSAGE } from '@shared/catchLength';
import LengthConfirmDialog from '../LengthConfirmDialog';
import { formatClubDateTime } from '../../utils/clubTime';

const client = generateClient({ authMode: 'userPool' });

const SPECIES_OPTIONS = [
  { value: 'FLATHEAD', label: 'Flathead' },
  { value: 'BREAM', label: 'Bream' },
  { value: 'JEWFISH', label: 'Jewfish' },
  { value: 'MANGROVE_JACK', label: 'Mangrove Jack' },
  { value: 'WHITING', label: 'Whiting' },
  { value: 'TAILOR', label: 'Tailor' },
  { value: 'TREVALLY', label: 'Trevally' },
  { value: 'OTHER', label: 'Other' },
];

const LEGAL_CATCH_SIZES: Record<string, number> = {
  FLATHEAD: 36,
  BREAM: 25,
  JEWFISH: 70,
  MANGROVE_JACK: 35,
  WHITING: 27,
  TAILOR: 30,
  TREVALLY: 25,
};

interface MemberCatchSubmissionProps {
  eventId: string;
}

const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const MemberCatchSubmission: React.FC<MemberCatchSubmissionProps> = ({ eventId }) => {
  const { userId } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [species, setSpecies] = useState('');
  const [lengthCm, setLengthCm] = useState('');
  // #85: confirm step for an unusually long catch; hard cap blocks submit.
  const [showLengthConfirm, setShowLengthConfirm] = useState(false);
  const lengthInputRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [keptOrReleased, setKeptOrReleased] = useState<'KEPT' | 'RELEASED'>('RELEASED');
  const [timeOfCatch, setTimeOfCatch] = useState(formatDateForInput(new Date()));
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [catcherMode, setCatcherMode] = useState<'me' | 'family' | 'other'>('me');
  const [selectedCompetitorName, setSelectedCompetitorName] = useState('');
  const [selectedFamilyMemberId, setSelectedFamilyMemberId] = useState<string | undefined>();
  const [checkedInMembers, setCheckedInMembers] = useState<Array<{ participantName: string; memberId?: string }>>([]);
  const [checkedInLoaded, setCheckedInLoaded] = useState(false);
  const [member, setMember] = useState<any>(null);
  const [familyMembers, setFamilyMembers] = useState<Array<{ name: string; memberId?: string; cognitoSub?: string }>>([]);
  const [memberSearch, setMemberSearch] = useState('');
  // Event timing, so the form can be gated the same way the server gates it.
  // Held as null until loaded; the gate treats "not yet known" as not-open so it
  // never briefly shows a form it is about to withdraw.
  const [eventTiming, setEventTiming] = useState<{
    competitionStartTime?: string | null;
    competitionEndTime?: string | null;
    gracePeriodMinutes?: number | null;
    adjudicatedAt?: string | null;
    cancelledAt?: string | null;
  } | null>(null);
  const [timingLoaded, setTimingLoaded] = useState(false);
  // Re-evaluated on a timer so the form opens by itself at fishing start rather
  // than requiring an angler standing on the bank at 06:59 to reload the page.
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 20_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!userId || !eventId) return;
    const load = async () => {
      try {
        // Event timing for the submission-window gate. Loaded first and
        // independently guarded: a failure here must not block the rest of the
        // form's data, but it also must not silently open the window — a failed
        // load leaves eventTiming null, which the gate treats as NOT_CONFIGURED
        // and therefore closed, matching the server's fail-closed behaviour.
        try {
          const { data: evt } = await client.models.Event.get({ id: eventId });
          if (evt) {
            setEventTiming({
              competitionStartTime: evt.competitionStartTime,
              competitionEndTime: evt.competitionEndTime,
              gracePeriodMinutes: evt.gracePeriodMinutes,
              adjudicatedAt: (evt as any).adjudicatedAt,
              cancelledAt: (evt as any).cancelledAt,
            });
          }
        } catch (timingErr) {
          console.error('Error loading competition timing:', timingErr);
        } finally {
          setTimingLoaded(true);
        }

        // Fetch logged-in member + family roster via the narrow server-side
        // projection (replaces Member.list/membersByFamilyGroup, which relied
        // on the broad allow.authenticated() model grant).
        const profileResult: any = await (client.queries as any).getOwnMemberProfile({});
        const profileResponse = parseLambdaResponse(profileResult?.data);
        const m = profileResponse?.success ? profileResponse.own : null;
        setMember(m);
        if (m?.familyGroupId) {
          const others = profileResponse.family || [];
          setFamilyMembers(others.map((fm: any) => ({
            name: `${fm.firstName} ${fm.lastName}`.trim(),
            memberId: fm.id,
            cognitoSub: fm.cognitoSub,
          })));
        } else if (m?.familyMembers) {
          try {
            const fm = typeof m.familyMembers === 'string' ? JSON.parse(m.familyMembers) : m.familyMembers || [];
            setFamilyMembers(fm);
          } catch { /* ignore */ }
        }

        // Fetch checked-in members for this event
        let allRegs: any[] = [];
        let nextToken: string | null = null;
        do {
          const result: any = await (client.models.CompetitionDayRegistration as any).list({
            filter: { eventId: { eq: eventId } },
            ...(nextToken ? { nextToken } : {}),
          });
          allRegs = allRegs.concat(result.data || []);
          nextToken = result.nextToken ?? null;
        } while (nextToken);

        const myName = m ? `${m.firstName} ${m.lastName}` : '';
        setCheckedInMembers(
          allRegs
            .filter((r: any) => r.participantName !== myName)
            .map((r: any) => ({ participantName: r.participantName, memberId: r.memberId }))
        );
        setCheckedInLoaded(true);
      } catch { /* non-critical */ }
    };
    load();
  }, [userId, eventId]);

  const showToast = (message: string, type: 'success' | 'error' | 'warning') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const legalSize = species ? LEGAL_CATCH_SIZES[species] : undefined;
  const lengthNum = parseFloat(lengthCm);
  const isBelowLegal = legalSize !== undefined && !isNaN(lengthNum) && lengthNum > 0 && lengthNum < legalSize;
  const isBlockedSubmission = isBelowLegal && keptOrReleased === 'KEPT';
  const isOverMax = !isNaN(lengthNum) && isOverMaxLength(lengthNum);
  const speciesLabel = SPECIES_OPTIONS.find((s) => s.value === species)?.label || species;

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setSpecies('');
    setLengthCm('');
    setPhoto(null);
    setPhotoPreview(null);
    setKeptOrReleased('RELEASED');
    setTimeOfCatch(formatDateForInput(new Date()));
    setSubmitted(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e?: React.FormEvent, lengthConfirmed = false) => {
    e?.preventDefault();

    if (!species || !lengthCm || !photo) {
      showToast('Please fill in all required fields and attach a photo.', 'error');
      return;
    }
    if (catcherMode !== 'me' && !selectedCompetitorName) {
      showToast('Please select who caught this fish.', 'error');
      return;
    }
    if (isNaN(lengthNum) || lengthNum <= 0) {
      showToast('Please enter a valid length in centimetres.', 'error');
      return;
    }
    if (isBlockedSubmission) {
      showToast(`This fish is below the legal catch size of ${legalSize}cm for ${species}. It must be marked as released.`, 'error');
      return;
    }
    if (isOverMax) {
      showToast(MAX_CATCH_LENGTH_MESSAGE, 'error');
      return;
    }
    if (!lengthConfirmed && isLengthUnusual(species, lengthNum)) {
      setShowLengthConfirm(true);
      return;
    }

    setSubmitting(true);
    try {
      // Compress photo client-side
      const imageCompression = (await import('browser-image-compression')).default;
      const compressed = await imageCompression(photo, {
        maxSizeMB: 2,
        maxWidthOrHeight: 2048,
        useWebWorker: true,
      });

      // 1. Get presigned upload URL
      const fileName = compressed.name || photo.name || 'catch-photo.jpg';
      const contentType = compressed.type || photo.type || 'image/jpeg';

      const uploadResult = await (client.queries as any).generateUploadUrl({
        purpose: 'CATCH_PHOTO',
        fileName,
        contentType,
        identityId: userId,
      });

      const uploadData = parseLambdaResponse<{ uploadUrl?: string; s3Key?: string }>(uploadResult?.data);
      if (!uploadData?.uploadUrl || !uploadData?.s3Key) {
        throw new Error('Failed to get upload URL');
      }

      // 2. Upload compressed photo to S3
      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: compressed,
      });
      if (!uploadResponse.ok) {
        throw new Error('Photo upload failed. Please try again.');
      }

      // 3. Submit catch
      const catchTime = new Date(timeOfCatch).toISOString();
      let competitorName = member ? `${member.firstName} ${member.lastName}` : '';
      if (catcherMode === 'family') competitorName = selectedCompetitorName;
      if (catcherMode === 'other') competitorName = selectedCompetitorName;

      // Resolve targetMemberId for on-behalf submissions
      let targetMemberId: string | undefined;
      if (catcherMode === 'family') {
        targetMemberId = selectedFamilyMemberId;
      } else if (catcherMode === 'other') {
        const match = checkedInMembers.find((m) => m.participantName === selectedCompetitorName);
        targetMemberId = match?.memberId;
      }

      const result = await (client.mutations as any).submitCatch({
        eventId,
        species,
        lengthCm: lengthNum,
        photoS3Key: uploadData.s3Key,
        keptOrReleased,
        timeOfCatch: catchTime,
        competitorName,
        targetMemberId,
      });

      if (result?.errors && result.errors.length > 0) {
        throw new Error(sanitise(result.errors[0].message));
      }

      /*
        Business failures come back in a 200 body as { success: false, error },
        NOT in result.errors — so checking result.errors alone is not enough.
        This is the same defect class fixed for competition-state transitions
        under #62/#63.

        Observed on dev 19 Sep 2026: submitCatch returned
          {"success":false,"error":"You must be registered for this competition
           to submit catches"}
        and this screen still showed "Catch Submitted! Your catch has been
        recorded and is pending review." The angler is told their catch is in
        when the server discarded it, and the real reason — not checked in,
        outside the fishing window, below a validation rule — is never shown.

        On competition day that silently loses catches, which is the worst
        possible failure for this screen.
      */
      const payload = parseLambdaResponse<{ success?: boolean; error?: string }>(result?.data);
      if (payload && payload.success === false) {
        throw new Error(
          sanitise(payload.error || 'Your catch could not be submitted. Please speak to an official.'),
        );
      }

      setSubmitted(true);
      showToast('Catch submitted successfully!', 'success');
    } catch (err: any) {
      console.error('Error submitting catch:', err);
      showToast(sanitise(err.message || 'Failed to submit catch. Please try again.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-8 px-4">
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">🎣</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Catch Submitted!</h2>
            <p className="text-gray-600 mb-6">Your catch has been recorded and counts towards the results. The top 6 in each category are checked by an official before prizes are awarded.</p>
            <div className="space-y-3">
              <button
                onClick={resetForm}
                className="w-full px-6 py-3 bg-fishing-blue text-white rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Submit Another Catch
              </button>
              <button
                onClick={() => { window.history.pushState({}, '', '/competitions'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg text-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Back to Competitions
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /*
    Submission-window gate. The server has always enforced this
    (submit-catch/rules.ts validateTimeWindow) but nothing gated the UI, so an
    angler before fishing start was shown the whole form, photographed the fish,
    entered species and length, submitted — and only then got "Competition has
    not started yet". Reported by the club 19 Sep 2026 as "submit catch is open
    early, should be 7am".

    Mirrors the server verdict via the shared lifecycle function, and fails
    closed while timing is still loading or could not be loaded.
  */
  const memberLifecycle = timingLoaded && eventTiming ? deriveLifecycle(eventTiming, now) : null;

  if (memberLifecycle && !memberLifecycle.catchesOpen) {
    const startsAt = eventTiming?.competitionStartTime
      ? new Date(eventTiming.competitionStartTime)
      : null;
    const endsAt = eventTiming?.competitionEndTime
      ? new Date(eventTiming.competitionEndTime)
      : null;
    const clubTime = (d: Date) =>
      formatClubDateTime(d, {
        timeZone: CLUB_TIMEZONE,
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

    const reason = memberLifecycle.catchReason;
    const heading =
      reason === 'NOT_STARTED'
        ? 'Catch submission is not open yet'
        : reason === 'ENDED' || reason === 'ADJUDICATED'
          ? 'Catch submission has closed'
          : reason === 'CANCELLED'
            ? 'This competition has been cancelled'
            : 'Catch submission is unavailable';

    const detail =
      reason === 'NOT_STARTED' && startsAt
        ? `Submissions open when fishing starts, ${clubTime(startsAt)}. Come back then — you can take your photos in the meantime and upload them once it opens.`
        : (reason === 'ENDED' || reason === 'ADJUDICATED') && endsAt
          ? `Submissions closed ${memberLifecycle.graceMinutes} minutes after fishing ended at ${clubTime(endsAt)}. Speak to an official if you have a catch to record.`
          : reason === 'CANCELLED'
            ? catchRefusalMessage(reason)
            : 'This competition has no fishing start and end time set, so submissions cannot be accepted. Please let an official know.';

    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-8 px-4">
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="text-4xl mb-3" aria-hidden="true">🎣</div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">{heading}</h1>
            <p className="text-sm text-gray-600">{detail}</p>
            <button
              onClick={() => {
                window.history.pushState({}, '', '/dashboard');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="mt-5 px-4 py-2 bg-fishing-blue hover:bg-blue-700 text-white text-sm font-medium rounded-md"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-8 px-4">
      {toast && (
        <div role="alert" aria-live="assertive" className={`fixed top-4 left-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all ${
          toast.type === 'success' ? 'bg-green-600' : toast.type === 'warning' ? 'bg-yellow-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}

      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-gray-900">Submit Catch</h1>
            <button
              onClick={() => { window.history.pushState({}, '', '/competitions'); window.dispatchEvent(new PopStateEvent('popstate')); }}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Back"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-gray-600 text-sm">Take a photo of your fish on the brag mat with the event sticker visible.</p>
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">📋 All catches are reviewed and approved by an admin. Photos are verified using AI to help ensure fair competition.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Who caught this fish? */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">Who caught this fish?</label>
            <div className={`grid ${familyMembers.length > 0 && checkedInLoaded ? 'grid-cols-3' : checkedInLoaded || familyMembers.length > 0 ? 'grid-cols-2' : 'grid-cols-1'} gap-2 mb-3`}>
              <button type="button" onClick={() => { setCatcherMode('me'); setSelectedCompetitorName(''); setMemberSearch(''); }}
                className={`py-3 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${catcherMode === 'me' ? 'border-fishing-blue bg-blue-50 text-fishing-blue' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                Me
              </button>
              {familyMembers.length > 0 && (
                <button type="button" onClick={() => { setCatcherMode('family'); setSelectedCompetitorName(''); setSelectedFamilyMemberId(undefined); setMemberSearch(''); }}
                  className={`py-3 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${catcherMode === 'family' ? 'border-fishing-blue bg-blue-50 text-fishing-blue' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                  My Family Member
                </button>
              )}
              {checkedInLoaded && (
                <button type="button" onClick={() => { setCatcherMode('other'); setSelectedCompetitorName(''); setMemberSearch(''); }}
                  className={`py-3 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${catcherMode === 'other' ? 'border-fishing-blue bg-blue-50 text-fishing-blue' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                  Another Member
                </button>
              )}
            </div>

            {catcherMode === 'family' && (
              <div className="grid grid-cols-2 gap-2">
                {familyMembers.map((fm: any) => (
                  <button key={fm.memberId || fm.name} type="button" onClick={() => { setSelectedCompetitorName(fm.name); setSelectedFamilyMemberId(fm.memberId); }}
                    className={`py-3 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${selectedCompetitorName === fm.name ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                    {fm.name}
                  </button>
                ))}
              </div>
            )}

            {catcherMode === 'other' && (
              <div>
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e: any) => setMemberSearch(e.target.value)}
                  placeholder="Search by name…"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fishing-blue mb-2"
                  aria-label="Search checked-in members"
                />
                {(() => {
                  const filtered = checkedInMembers.filter((m: any) =>
                    m.participantName.toLowerCase().includes(memberSearch.toLowerCase())
                  );
                  if (filtered.length === 0) {
                    return <p className="text-sm text-gray-500 italic">No members found</p>;
                  }
                  return (
                    <div className="flex flex-wrap gap-2">
                      {filtered.map((m: any) => (
                        <button key={m.participantName} type="button" onClick={() => setSelectedCompetitorName(m.participantName)}
                          className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${selectedCompetitorName === m.participantName ? 'bg-fishing-blue text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                          {m.participantName}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {catcherMode !== 'me' && selectedCompetitorName && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-fishing-blue">
                <div className="font-semibold">Submitting on behalf of {selectedCompetitorName}</div>
                <div className="text-xs text-blue-700 mt-1">
                  This catch will be counted towards {selectedCompetitorName}’s score, not yours.
                </div>
              </div>
            )}
          </div>

          {/* Photo Capture */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">Catch Photo *</label>
            {photoPreview ? (
              <div className="relative mb-3">
                <img src={photoPreview} alt="Catch preview" className="w-full rounded-lg object-cover max-h-64" />
                <button
                  type="button"
                  onClick={() => { setPhoto(null); setPhotoPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1.5 shadow-lg"
                  aria-label="Remove photo"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-8 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors flex flex-col items-center justify-center"
              >
                <svg className="h-12 w-12 text-fishing-blue mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-fishing-blue font-medium text-lg">Take Photo or Upload</span>
                <span className="text-gray-500 text-sm mt-1">Show fish on brag mat with event sticker</span>
              </button>
            )}
            {/*
              No `capture` attribute, deliberately. `capture="environment"` made
              mobile browsers open the rear camera immediately and skip the photo
              library entirely, so an angler could not submit a shot already taken
              — the common case when they photographed the fish at the water and
              only reached signal or the app later. Removed 19 Sep 2026 on club
              request.

              It was never an anti-cheat control: the specs describe it only as
              "direct camera access" (a convenience), and `capture` is a hint that
              is trivially bypassed and ignored entirely on desktop. Photo
              integrity is handled by verify-catch-ai (sticker match and
              suspicion scoring), not by which picker opens.

              Without the attribute the OS picker still offers the camera, so
              taking a photo there and then remains one tap away.
            */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
          </div>

          {/* Species */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">Species *</label>
            <div className="grid grid-cols-2 gap-3">
              {SPECIES_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSpecies(opt.value)}
                  className={`py-3 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${
                    species === opt.value
                      ? 'border-fishing-blue bg-blue-50 text-fishing-blue'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {species === 'OTHER' && (
              <p className="mt-3 text-xs text-gray-600">
                "Other" species are subject to NSW DPI size limits but may not be eligible for competition prizes.
              </p>
            )}
          </div>

          {/* Length */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label htmlFor="lengthCm" className="block text-sm font-semibold text-gray-900 mb-3">
              Length (cm) *
            </label>
            <input
              id="lengthCm"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.1"
              ref={lengthInputRef}
              value={lengthCm}
              onChange={(e) => setLengthCm(e.target.value)}
              aria-invalid={isOverMax || undefined}
              aria-describedby={isOverMax ? 'lengthCm-error' : undefined}
              placeholder="Enter length in centimetres"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue focus:border-fishing-blue"
            />
            {isOverMax && (
              <p id="lengthCm-error" role="alert" className="mt-3 text-sm font-medium text-red-700">
                {MAX_CATCH_LENGTH_MESSAGE}
              </p>
            )}
            {isBelowLegal && (
              <div className={`mt-3 p-3 rounded-lg ${isBlockedSubmission ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <p className={`text-sm font-medium ${isBlockedSubmission ? 'text-red-800' : 'text-yellow-800'}`}>
                  {isBlockedSubmission
                    ? `⛔ This fish is below the legal catch size of ${legalSize}cm for ${SPECIES_OPTIONS.find(s => s.value === species)?.label}. It must be marked as released.`
                    : `⚠️ This fish is below the legal catch size of ${legalSize}cm for ${SPECIES_OPTIONS.find(s => s.value === species)?.label}. Please ensure it was released.`}
                </p>
              </div>
            )}
          </div>

          {/* Kept/Released */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">Kept or Released *</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setKeptOrReleased('RELEASED')}
                className={`py-3 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${
                  keptOrReleased === 'RELEASED'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                🐟 Released
              </button>
              <button
                type="button"
                onClick={() => setKeptOrReleased('KEPT')}
                className={`py-3 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${
                  keptOrReleased === 'KEPT'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                🪣 Kept
              </button>
            </div>
          </div>

          {/* Time of Catch */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label htmlFor="timeOfCatch" className="block text-sm font-semibold text-gray-900 mb-3">
              Time of Catch
            </label>
            <input
              id="timeOfCatch"
              type="datetime-local"
              value={timeOfCatch}
              onChange={(e) => setTimeOfCatch(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue focus:border-fishing-blue"
            />
          </div>

          {/* AI processing disclosure */}
          <p className="text-xs text-red-600 text-center">
            Photos are processed by AI for verification purposes.
          </p>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || isBlockedSubmission || isOverMax || !species || !lengthCm || !photo}
            className="w-full py-4 bg-fishing-blue text-white rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed shadow-lg"
          >
            {submitting ? 'Submitting...' : 'Submit Catch'}
          </button>
        </form>
        <LengthConfirmDialog
          open={showLengthConfirm}
          lengthCm={lengthNum}
          speciesLabel={speciesLabel}
          onFixLength={() => {
            setShowLengthConfirm(false);
            lengthInputRef.current?.focus();
          }}
          onConfirm={() => {
            setShowLengthConfirm(false);
            handleSubmit(undefined, true);
          }}
        />
      </div>
    </div>
  );
};

export default MemberCatchSubmission;
